// Breeding panel — Phaser-rendered modal. Listed allies are clickable;
// player picks two, confirms, and the panel produces a (parentA, parentB)
// pair for the scene to apply. ESC or "cancel" closes without breeding.

import Phaser from 'phaser';
import type { WorldState } from '../sim/world-state';
import { C } from '../sim/components';
import type {
  GenomeComp,
  FactionComp,
  Age,
  Fertility,
  Stats,
  EmergentComp,
} from '../sim/components';
import { isFertile } from '../sim/systems/lifecycle';
import { isTouchDevice } from '../input/device';

/** Per-device sizing constants so touch users get 44px+ targets. */
function sizing() {
  const touch = isTouchDevice();
  return {
    titleFont: touch ? '24px' : '22px',
    helpFont: touch ? '14px' : '12px',
    rowH: touch ? 48 : 28,
    rowFont: touch ? '16px' : '11px',
    rowIdFont: touch ? '15px' : '12px',
    btnH: touch ? 48 : 28,
    btnFont: touch ? '17px' : '13px',
    btnPadX: touch ? 18 : 10,
  };
}

interface Candidate {
  id: number;
  emergent: string[];
  agePct: number;
  damage: number;
  health: number;
  speed: number;
}

export interface BreedingPanelResult {
  parentAId: number;
  parentBId: number;
  /** If set, auto-breed this many more pickups after this one without a panel. */
  autoQueue?: number;
}

/** Returned when fewer than 2 fertile parents exist — caller should spawn a random ally + heal. */
export interface BreedingPanelFallback {
  type: 'fallback';
}

export class BreedingPanel {
  private container?: Phaser.GameObjects.Container;
  private candidates: Candidate[] = [];
  private selected: number[] = []; // up to two entity ids
  private rows: Map<number, Phaser.GameObjects.Container> = new Map();
  private compText?: Phaser.GameObjects.Text;
  private resolveFn?: (result: BreedingPanelResult | null) => void;

  constructor(private readonly scene: Phaser.Scene) {}

  /** Show the panel. Resolves with parent ids on confirm, null on cancel, or fallback when not enough fertile parents. */
  open(ws: WorldState): Promise<BreedingPanelResult | BreedingPanelFallback | null> {
    this.candidates = collectFertileCandidates(ws);
    this.selected = [];
    this.rows.clear();

    if (this.candidates.length < 2) {
      // Not enough fertile parents — signal caller to spawn a random ally and heal instead.
      return Promise.resolve({ type: 'fallback' });
    }

    return new Promise((resolve) => {
      this.resolveFn = resolve;
      this.build();
    });
  }

  isOpen(): boolean {
    return !!this.container;
  }

  destroy(): void {
    this.container?.destroy();
    this.container = undefined;
    this.compText = undefined;
    this.rows.clear();
  }

  private build(): void {
    const { width, height } = this.scene.scale.gameSize;
    const c = this.scene.add.container(0, 0).setDepth(2000);

    // Dim background.
    const bg = this.scene.add.rectangle(width / 2, height / 2, width, height, 0x000000, 0.55);
    bg.setInteractive(); // swallow clicks
    c.add(bg);

    // Panel.
    const panelW = Math.min(720, width - 80);
    const panelH = Math.min(540, height - 80);
    const panelX = (width - panelW) / 2;
    const panelY = (height - panelH) / 2;
    const panel = this.scene.add
      .rectangle(panelX, panelY, panelW, panelH, 0x1a1d23, 1)
      .setOrigin(0, 0)
      .setStrokeStyle(2, 0xff9ec4);
    c.add(panel);

    const sz = sizing();
    const touch = isTouchDevice();

    const title = this.scene.add.text(panelX + 20, panelY + 18, 'Choose two parents', {
      fontFamily: 'system-ui',
      fontSize: sz.titleFont,
      color: '#ffffff',
    });
    c.add(title);

    const helpText = touch
      ? 'tap two balls to select. tap confirm to breed.'
      : 'click two balls to select. ENTER to confirm. ESC to cancel.';
    const help = this.scene.add.text(panelX + 20, panelY + (touch ? 56 : 52), helpText, {
      fontFamily: 'system-ui',
      fontSize: sz.helpFont,
      color: '#9aa0a6',
    });
    c.add(help);

    const mechanic = this.scene.add.text(
      panelX + 20,
      panelY + (touch ? 76 : 68),
      'Each gene blends both parents (rounded average). ~5% chance per gene of ±1 mutation.',
      { fontFamily: 'system-ui', fontSize: sz.helpFont, color: '#6a7080' },
    );
    c.add(mechanic);

    // Candidate rows.
    const listTop = panelY + (touch ? 118 : 102);
    const btnAreaH = sz.btnH + 32;
    const maxRows = Math.floor((panelH - (listTop - panelY) - btnAreaH) / sz.rowH);
    const rendered = this.candidates.slice(0, maxRows);
    rendered.forEach((cand, i) => {
      const row = this.makeRow(cand, panelX + 16, listTop + i * sz.rowH, panelW - 32, sz.rowH - 4);
      c.add(row);
      this.rows.set(cand.id, row);
    });
    if (this.candidates.length > maxRows) {
      const more = this.scene.add.text(
        panelX + 20,
        listTop + maxRows * sz.rowH + 4,
        `+ ${this.candidates.length - maxRows} more fertile (not shown)`,
        { fontFamily: 'system-ui', fontSize: sz.helpFont, color: '#9aa0a6' },
      );
      c.add(more);
    }

    // Parent comparison — updates dynamically as selections change.
    const compY = panelY + panelH - btnAreaH - 20;
    const compT = this.scene.add.text(panelX + 20, compY, 'Select two parents to compare.', {
      fontFamily: 'system-ui',
      fontSize: sz.helpFont,
      color: '#6a7080',
    });
    c.add(compT);
    this.compText = compT;

    // Buttons: [cancel] [auto-pick] [auto (10)] ... [confirm]
    const btnY = panelY + panelH - sz.btnH - 16;
    const cancelW = touch ? 100 : 90;
    const autoW = touch ? 130 : 110;
    const auto10W = touch ? 130 : 110;
    const confirmW = touch ? 120 : 90;
    const cancelBtn = this.makeButton(panelX + 16, btnY, cancelW, sz.btnH, 'cancel', () =>
      this.resolve(null),
    );
    c.add(cancelBtn);
    const autoBtn = this.makeButton(
      panelX + 16 + cancelW + 10,
      btnY,
      autoW,
      sz.btnH,
      'auto-pick',
      () => {
        const auto = autoPickPair(this.candidates);
        if (auto) this.resolve(auto);
      },
    );
    c.add(autoBtn);
    const auto10Btn = this.makeButton(
      panelX + 16 + cancelW + 10 + autoW + 10,
      btnY,
      auto10W,
      sz.btnH,
      'auto (10)',
      () => {
        const auto = autoPickPair(this.candidates);
        if (auto) this.resolve({ ...auto, autoQueue: 9 });
      },
    );
    c.add(auto10Btn);
    const confirmBtn = this.makeButton(
      panelX + panelW - confirmW - 16,
      btnY,
      confirmW,
      sz.btnH,
      'confirm',
      () => this.tryConfirm(),
    );
    c.add(confirmBtn);

    // Keyboard.
    this.scene.input.keyboard?.once('keydown-ESC', () => this.resolve(null));
    this.scene.input.keyboard?.once('keydown-ENTER', () => this.tryConfirm());

    this.container = c;
  }

  private makeRow(
    cand: Candidate,
    x: number,
    y: number,
    w: number,
    h: number,
  ): Phaser.GameObjects.Container {
    const sz = sizing();
    const row = this.scene.add.container(x, y);
    const bg = this.scene.add
      .rectangle(0, 0, w, h, 0x252832, 1)
      .setOrigin(0, 0)
      .setStrokeStyle(1, 0x3a3f4a);
    row.add(bg);

    const padY = Math.max(4, (h - 14) / 2);
    const idLabel = this.scene.add.text(10, padY, `#${cand.id}`, {
      fontFamily: 'system-ui',
      fontSize: sz.rowIdFont,
      color: '#ffffff',
    });
    row.add(idLabel);

    const stats = this.scene.add.text(
      72,
      padY,
      `dmg ${cand.damage.toFixed(1)}   hp ${cand.health.toFixed(1)}   spd ${cand.speed.toFixed(0)}   age ${(cand.agePct * 100).toFixed(0)}%`,
      { fontFamily: 'system-ui', fontSize: sz.rowFont, color: '#c8c8c8' },
    );
    row.add(stats);

    if (cand.emergent.length > 0) {
      const e = this.scene.add.text(w - 10, padY, cand.emergent.join(' · '), {
        fontFamily: 'system-ui',
        fontSize: sz.rowFont,
        color: '#ff9ec4',
      });
      e.setOrigin(1, 0);
      row.add(e);
    }

    bg.setInteractive({ useHandCursor: true });
    bg.on('pointerup', () => this.toggle(cand.id));

    return row;
  }

  private toggle(id: number): void {
    const i = this.selected.indexOf(id);
    if (i >= 0) {
      this.selected.splice(i, 1);
    } else {
      if (this.selected.length >= 2) {
        // Drop the oldest selection.
        const dropped = this.selected.shift()!;
        this.repaintRow(dropped);
      }
      this.selected.push(id);
    }
    this.repaintRow(id);
    this.updateCompText();
  }

  private updateCompText(): void {
    if (!this.compText) return;
    if (this.selected.length === 2) {
      const a = this.candidates.find((c) => c.id === this.selected[0]);
      const b = this.candidates.find((c) => c.id === this.selected[1]);
      if (a && b) {
        const childDmg = (a.damage + b.damage) / 2;
        const childSpd = (a.speed + b.speed) / 2;
        const childHp = (a.health + b.health) / 2;
        this.compText.setText(
          `A: dmg ${a.damage.toFixed(1)}  spd ${a.speed.toFixed(0)}  hp ${a.health.toFixed(1)}` +
            `    B: dmg ${b.damage.toFixed(1)}  spd ${b.speed.toFixed(0)}  hp ${b.health.toFixed(1)}` +
            `    →  child ≈ dmg ${childDmg.toFixed(1)}  spd ${childSpd.toFixed(0)}  hp ${childHp.toFixed(1)} (± mutation)`,
        );
        this.compText.setColor('#b8d898');
      }
    } else {
      this.compText.setText('Select two parents to compare.');
      this.compText.setColor('#6a7080');
    }
  }

  private repaintRow(id: number): void {
    const row = this.rows.get(id);
    if (!row) return;
    const bg = row.list[0] as Phaser.GameObjects.Rectangle;
    if (this.selected.includes(id)) {
      bg.setFillStyle(0x3a2a36, 1);
      bg.setStrokeStyle(2, 0xff9ec4);
    } else {
      bg.setFillStyle(0x252832, 1);
      bg.setStrokeStyle(1, 0x3a3f4a);
    }
  }

  private tryConfirm(): void {
    if (this.selected.length !== 2) return;
    this.resolve({ parentAId: this.selected[0]!, parentBId: this.selected[1]! });
  }

  private makeButton(
    x: number,
    y: number,
    w: number,
    h: number,
    label: string,
    onClick: () => void,
  ): Phaser.GameObjects.Container {
    const sz = sizing();
    const c = this.scene.add.container(x, y);
    const rect = this.scene.add
      .rectangle(0, 0, w, h, 0x3a3f4a, 1)
      .setOrigin(0, 0)
      .setStrokeStyle(1, 0x6a6f7a);
    const t = this.scene.add
      .text(w / 2, h / 2, label, {
        fontFamily: 'system-ui',
        fontSize: sz.btnFont,
        color: '#ffffff',
      })
      .setOrigin(0.5);
    rect.setInteractive({ useHandCursor: true });
    rect.on('pointerover', () => rect.setFillStyle(0x4a4f5a, 1));
    rect.on('pointerout', () => rect.setFillStyle(0x3a3f4a, 1));
    rect.on('pointerup', () => onClick());
    c.add([rect, t]);
    return c;
  }

  private resolve(value: BreedingPanelResult | null): void {
    const fn = this.resolveFn;
    this.resolveFn = undefined;
    this.destroy();
    fn?.(value);
  }
}

export function collectFertileCandidates(ws: WorldState): Candidate[] {
  const { world } = ws;
  const factions = world.store<FactionComp>(C.faction);
  const ages = world.store<Age>(C.age);
  const fert = world.store<Fertility>(C.fertility);
  const stats = world.store<Stats>(C.stats);
  const emergent = world.store<EmergentComp>(C.emergent);
  const out: Candidate[] = [];
  factions.forEach((f, id) => {
    if (f.side !== 'ally') return;
    const a = ages.get(id);
    const fw = fert.get(id);
    if (!a || !fw) return;
    if (!isFertile(a, fw.start, fw.end)) return;
    const s = stats.get(id);
    const e = emergent.get(id);
    if (!s) return;
    out.push({
      id,
      emergent: e?.ids ?? [],
      agePct: a.lifespan > 0 ? a.seconds / a.lifespan : 0,
      damage: s.sheet.attackDamage,
      health: s.sheet.maxHealth,
      speed: s.sheet.speed,
    });
  });
  // Sort by emergent count first (most interesting), then damage.
  out.sort((a, b) => b.emergent.length - a.emergent.length || b.damage - a.damage);
  // Quiet unused warning when no genome inspection is shown yet.
  void world.store<GenomeComp>(C.genome);
  return out;
}

export function autoPickPair(candidates: Candidate[]): BreedingPanelResult | null {
  if (candidates.length < 2) return null;
  // Pick the two with highest combined damage + speed and complementary emergents.
  const scored = candidates.map((c) => ({
    c,
    score: c.damage * 2 + c.speed * 0.5 + c.health * 0.5 + c.emergent.length * 4,
  }));
  scored.sort((a, b) => b.score - a.score);
  return { parentAId: scored[0]!.c.id, parentBId: scored[1]!.c.id };
}
